import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import * as Icons from "lucide-react";
import { ArrowRight, Sparkles, GraduationCap, Activity } from "lucide-react";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";

export default function Home() {
  const { t, lang } = useLang();
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/regions")
      .then((r) => setRegions(r.data))
      .catch(() => toast.error(t("home.errLoad")))
      .finally(() => setLoading(false));
  }, [lang, t]);

  const totalTests = regions.reduce((s, r) => s + r.test_count, 0);

  return (
    <div data-testid="home-page">
      <section className="mb-14 sm:mb-20 rise">
        <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-4 font-mono">
          <span className="title-rule" />
          {t("home.eyebrow")}
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05] max-w-3xl">
          {t("home.hero1")} <em className="text-accent not-italic font-serif italic">{t("home.heroAccent")}</em>{" "}
          <br className="hidden sm:block" /> {t("home.hero2")}
        </h1>
        <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed">
          {t("home.lead")}
        </p>
        <div className="mt-7 flex flex-wrap gap-3">
          <Link
            to="/fall"
            data-testid="hero-cta-cases"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-full text-sm font-medium hover:bg-primary/90 transition-all hover:gap-3"
          >
            {t("home.ctaCases")} <ArrowRight className="size-4" />
          </Link>
          <Link
            to="/differentialdiagnos"
            data-testid="hero-cta-dx"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-accent text-accent-foreground rounded-full text-sm font-medium hover:bg-accent/90 transition-colors"
          >
            <Activity className="size-4" /> {t("home.ctaDx")}
          </Link>
          <Link
            to="/flashcards"
            data-testid="hero-cta-flash"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-card border border-border rounded-full text-sm font-medium hover:bg-muted transition-colors"
          >
            <Sparkles className="size-4 text-accent" /> {t("home.ctaFlash")}
          </Link>
        </div>
        <div className="mt-10 flex items-center gap-8 text-sm">
          <div>
            <div className="font-serif text-3xl font-medium text-primary">{regions.length}</div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">{t("home.statRegions")}</div>
          </div>
          <div className="h-10 w-px bg-border" />
          <div>
            <div className="font-serif text-3xl font-medium text-primary">{totalTests}</div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">{t("home.statTests")}</div>
          </div>
          <div className="h-10 w-px bg-border" />
          <div>
            <div className="font-serif text-3xl font-medium text-accent">AI</div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Sonnet 4.5</div>
          </div>
        </div>
      </section>

      <section className="mb-12">
        <div className="flex items-end justify-between mb-6">
          <h2 className="font-serif text-2xl sm:text-3xl font-medium">
            <span className="title-rule" /> {t("home.chooseRegion")}
          </h2>
          <Link to="/quiz" className="text-sm text-primary hover:text-accent flex items-center gap-1.5" data-testid="link-quiz">
            <GraduationCap className="size-4" /> {t("home.trainQuiz")}
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={`skel-${i}`} className="h-36 bg-muted/50 rounded-md animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {regions.map((r, i) => {
              const Icon = Icons[r.icon] || Icons.Circle;
              return (
                <Link to={`/regioner/${r.id}`} key={r.id} data-testid={`region-card-${r.id}`} className={`rise rise-${(i % 5) + 1}`}>
                  <Card className="group relative overflow-hidden p-5 h-full hover:border-primary/40 hover:shadow-md transition-all bg-card cursor-pointer">
                    <div className="flex items-start justify-between mb-3">
                      <div className="size-11 rounded-md bg-secondary text-primary flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                        <Icon className="size-5" />
                      </div>
                      <span className="text-[11px] font-mono text-muted-foreground bg-muted/60 px-2 py-1 rounded">
                        {r.test_count} {t("home.testsCount")}
                      </span>
                    </div>
                    <h3 className="font-serif text-xl font-medium tracking-tight mb-1.5 group-hover:text-primary transition-colors">
                      {r.name}
                    </h3>
                    <p className="text-sm text-muted-foreground line-clamp-2">{r.description}</p>
                    <div className="mt-4 inline-flex items-center text-xs text-accent gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {t("home.openRegion")} <ArrowRight className="size-3" />
                    </div>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </section>

      <section className="mt-16 grid lg:grid-cols-5 gap-8 items-start">
        <div className="lg:col-span-2">
          <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-3 font-mono">
            <span className="title-rule" />{t("home.methodologyEyebrow")}
          </div>
          <h2 className="font-serif text-3xl font-medium leading-tight">
            {t("home.methodologyTitle")}
          </h2>
        </div>
        <div className="lg:col-span-3 space-y-4 text-sm leading-relaxed text-foreground/80">
          <p>{t("home.methodologyText1")}</p>
          <p>{t("home.methodologyText2")}</p>
        </div>
      </section>
    </div>
  );
}
